import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../context/LoadingContext";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";
import { Modal, Button } from "react-bootstrap";
import DataTable from "../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../pages/Requirement/components/common/SearchFilter";
import { fetchData } from "../../../pages/Requirement/services/RequirementServices";
import dayjs from "dayjs";
// Utility function to format date from "2025-09-30 00:00:00" to "09/30/25"
const formatDateToMMDDYY = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString.replace(" ", "T"));
  if (isNaN(date)) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

const ApprovedProduce = () => {
  const navigate = useNavigate();
  const { setLoading, loading } = useLoading();

  // State variables
  const [activeTab, setActiveTab] = useState("commodityDetails");
  const [sellerItemDetails, setSellerItemDetails] = useState([]);
  const [selectedSellerItemDetail, setSelectedSellerItemDetail] =
    useState(null);
  const [currentSellerItemDetailPage, setCurrentSellerItemDetailPage] =
    useState(1);
  const [totalSellerItemDetailPages, setTotalSellerItemDetailPages] =
    useState(1);
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [products, setProducts] = useState([]);
  const [states, setStates] = useState([]);
  const [filterTerms, setFilterTerms] = useState({
    product: "",
    grade: "",
    state: "",
    approved: "Yes",
    ExpiresGT: `'${dayjs().format("YYYY-MM-DD")}'`,
  });
  const [showModal, setShowModal] = useState(false);
  const [showModalMsg] = useState("");

  const pageSize = 20;

  // Search fields configuration for SearchFilter component
  const fields = [
    {
      name: "product",
      label: "Product",
      type: "dropdown",
      options: [
        { value: "", label: "Select a product" },
        ...[...new Set(products.map((product) => product.ProductName))].map(
          (product) => ({
            value: product,
            label: product,
          })
        ),
      ],
      value: filterTerms.product,
      onChange: (value) => {
        setFilterTerms((prev) => ({
          ...prev,
          product: value,
          grade: "", // Reset grade when product changes
        }));
      },
    },
    {
      name: "grade",
      label: "Grade",
      type: "dropdown",
      options: [
        { value: "", label: "Select a Grade" },
        ...products
          .filter((product) => product.ProductName === filterTerms.product)
          .map((product) => ({
            value: product.Grade,
            label: product.Grade,
          })),
      ],
      value: filterTerms.grade,
      onChange: (value) => {
        setFilterTerms((prev) => ({ ...prev, grade: value }));
      },
      disabled: !filterTerms.product,
    },
    {
      name: "state",
      label: "State",
      type: "dropdown",
      options: [
        { value: "", label: "Select State" },
        ...states.map((state) => ({
          value: state,
          label: state,
        })),
      ],
      value: filterTerms.state,
      onChange: (value) => {
        setFilterTerms((prev) => ({ ...prev, state: value }));
      },
    },
    // {
    //   name: "approved",
    //   label: "Approved",
    //   type: "dropdown",
    //   options: [
    //     { value: "", label: "All" },
    //     { value: "Yes", label: "Yes" },
    //     { value: "No", label: "No" },
    //   ],
    //   value: filterTerms.approved,
    //   onChange: (value) => {
    //     setFilterTerms((prev) => ({ ...prev, approved: value }));
    //   },
    // },
  ];

  // Define table columns for DataTable
  const columns = [
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    { id: "Quantity", numeric: true, label: "Quantity" },
    { id: "Price", numeric: true, label: "Price" },
    { id: "State", numeric: false, label: "State" },
    { id: "SellerType", numeric: false, label: "Seller Type" },
    { id: "SellerEmailID", numeric: false, label: "Seller Name" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created On",
      //renderCell: (row) => formatDateToMMDDYY(row.CreatedAt),
      renderCell: (row) =>
              row.CreatedAt
                ? dayjs(row.CreatedAt).format("DD/MM/YYYY") 
                : "",
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires On",
      renderCell: (row) =>
              row.Expires
                ? dayjs(row.Expires).format("DD/MM/YYYY") 
                : "",
    },
    // { id: "Approved", numeric: false, label: "Approved" },
  ];

  // Handle row click for DataTable
  const handleRowClick = useCallback((row) => {
    handleSellerItemDetailRowClick(row);
  }, []);

  // Handle page change for DataTable
  const handlePageChange = useCallback((page) => {
    setCurrentSellerItemDetailPage(page);
  }, []);

  const currentPage = currentSellerItemDetailPage;

  // Fetch seller item details
  const fetchSellerItemDetails = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        target: "listSellerItemDetailsAdm",
        filterTerms: filterTerms,
        UserOrder: "CreatedAt  DESC",
        rowStart: (currentSellerItemDetailPage - 1) * pageSize,
        pageSize: pageSize,
      };

      const result = await fetchData(body);
      if (result) {
        result.items = result.items.map((item) => ({
          ...item,
          id: item.SellerItemID,
        }));
        setSellerItemDetails(result.items || []);
        const totalItems = result.total || 0;
        setTotalSellerItemDetailPages(Math.ceil(totalItems / pageSize));
        if (result.items && result.items.length > 0) {
          const firstItem = result.items[0];
          setSelectedSellerItemDetail(firstItem);
          setSelectedProductName(firstItem.ProductName);
          setSelectedGrade(firstItem.Grade);
        } else {
          // Clear selection when no items are available
          setSelectedSellerItemDetail(null);
          setSelectedProductName("");
          setSelectedGrade("");
        }

        if (result.items && result.items.length > 0) {
          setSelectedSellerItemDetail(result.items[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching seller item details:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSellerItemDetailPage, filterTerms, setLoading]);

  // Fetch products and states
  const fetchProducts = useCallback(async () => {
    try {
      const body = {
        target: "listProducts",
        rowStart: 0,
        pageSize: 1000,
      };

      const result = await fetchData(body);
      if (result) {
        setProducts(result.items || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, []);

  const fetchStates = useCallback(async () => {
    try {
      const body = {
        target: "listDropDownOptions",
        filterTerms: { Type: "State" },
        rowStart: 0,
        pageSize: 1000,
      };

      const result = await fetchData(body);
      if (result) {
        const stateOptions = result.items || [];
        setStates(stateOptions.map((state) => state.Value));
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  }, []);

  // Event handlers
  const handleSellerItemDetailRowClick = (sellerItemDetail) => {
    setSelectedSellerItemDetail(sellerItemDetail);
    setSelectedProductName(sellerItemDetail.ProductName);
    setSelectedGrade(sellerItemDetail.Grade);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Handle search for SearchFilter component
  const handleSearchFilter = (searchValues) => {
    setFilterTerms(searchValues);
    setCurrentSellerItemDetailPage(1);
  };

  // Effects
  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        }
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    fetchProducts();
    fetchStates();
  }, [navigate, fetchProducts, fetchStates]);

  useEffect(() => {
    fetchSellerItemDetails();
  }, [fetchSellerItemDetails]);

  return (
    <div className="App card">
      <div className="main-wrapper">
        <div className="content">
          <main className="main-content">
            <div className="search-filter4">
              <div className="title4">
                <div className="head-text">Approved</div>
              </div>
              <div className="filter4">
                <SearchFilter fields={fields} onSearch={handleSearchFilter} />
              </div>
            </div>

            <div className="offers-sec">
              {loading && <div className="loading">Loading offers...</div>}
              {!loading && sellerItemDetails.length > 0 && (
                <DataTable
                  columns={columns}
                  data={sellerItemDetails}
                  total={totalSellerItemDetailPages * pageSize}
                  title="Approved Produce Information"
                  rowsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onRowClick={handleRowClick}
                  currentPage={currentPage}
                  initialOrderBy="CreatedAt"
                  selectedRows={selectedSellerItemDetail ? [selectedSellerItemDetail.SellerItemID] : []}
                />
              )}
              {!loading && sellerItemDetails.length === 0 && (
                <div className="empty-state">No Seller items available</div>
              )}
            </div>

            <section>
              <div className="tabs">
                <button
                  className={activeTab === "commodityDetails" ? "active" : ""}
                  onClick={() => handleTabClick("commodityDetails")}
                >
                  Commodity Details
                </button>
                <button
                  className={activeTab === "Specifications" ? "active" : ""}
                  onClick={() => handleTabClick("Specifications")}
                >
                  Specifications
                </button>

                <button
                  className={activeTab === "pricing" ? "active" : ""}
                  onClick={() => handleTabClick("pricing")}
                >
                  Pricing, Payment & Guarantee
                </button>
              </div>

              {activeTab === "commodityDetails" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-9 col-sm-9 col-6">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="product">Category</label>
                          <input
                            id="category"
                            type="text"
                            value={selectedSellerItemDetail?.CategoryName || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="product">Product</label>
                          <input
                            id="grade"
                            type="product"
                            value={selectedProductName || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="hsCode">HS Code</label>
                          <input
                            id="hsCode"
                            type="text"
                            value={selectedSellerItemDetail?.HsCode || ""}
                            className="form-control"
                            readOnly
                            placeholder=""
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="grade">Grade / Quality</label>
                          <input
                            id="grade"
                            type="text"
                            value={selectedGrade || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="variety">Variety</label>
                          <input
                            id="variety"
                            type="text"
                            value={selectedSellerItemDetail?.Variety || ""}
                            className="form-control"
                            readOnly
                            placeholder=""
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="offerExpiryDate">Expiry Date</label>
                          <input
                            id="Expires"
                            type="text"
                            value={formatDateToMMDDYY(
                              selectedSellerItemDetail?.Expires
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="offerExpiryDate">Harvest Date</label>
                          <input
                            id="HarvestYear"
                            type="text"
                            value={formatDateToMMDDYY(
                              selectedSellerItemDetail?.HarvestYear
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="offeredQuantity">Quantity</label>
                          <input
                            id="offeredQuantity"
                            type="number"
                            value={selectedSellerItemDetail?.Quantity || ""}
                            className="form-control"
                            readOnly
                            placeholder="Numeric"
                          />
                        </div>

                        <div className="col-lg-6 col-sm-4 col-12">
                          <label htmlFor="originCountry">Origin Country</label>
                          <input
                            id="Country "
                            type="text"
                            value={selectedSellerItemDetail?.Country || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="state">State</label>
                          <input
                            id="state"
                            type="text"
                            value={selectedSellerItemDetail?.State || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Specifications" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-9 col-sm-9 col-6">
                      <div className="row">
                        <div className="col-lg-12 col-sm-12 col-12">
                          <label htmlFor="productSpecifications">
                            Product Specifications
                          </label>
                          <textarea
                            id="productSpecifications"
                            name="productSpecifications"
                            maxLength="4000"
                            className="form-control"
                            rows="4"
                            value={
                              selectedSellerItemDetail?.productSpecifications ||
                              ""
                            }
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "pricing" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-9 col-sm-9 col-6">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label>Currency</label>
                          <input
                            id="Currency "
                            type="text"
                            value={selectedSellerItemDetail?.Currency || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label>
                            Price/ MT ({selectedSellerItemDetail?.Currency})
                          </label>
                          <input
                            id="Price"
                            type="text"
                            value={selectedSellerItemDetail?.Price || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label>
                            Total Price ({selectedSellerItemDetail?.Currency})
                          </label>
                          <input
                            id="Price"
                            type="text"
                            value={selectedSellerItemDetail?.TotalPrice || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label>Payment Method</label>
                          <input
                            id="PaymentMethod"
                            type="text"
                            value={selectedSellerItemDetail?.Payment || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </main>
        </div>
      </div>

      {/* Modal for messages */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>{showModalMsg}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApprovedProduce;
